import {
  getMembershipConfig,
  MemberProfile,
} from "@/services/core-api-adapter";
import {
  FeatureToggleIdentifier,
  isFeatureEnabled,
} from "@/services/feature-toggle-adapter";
import theme, { panelStyle, shadows } from "@/theme";
import { convertPxToRem } from "@/utils";
import { Box, Stack, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { trackEvent, trackUserInteraction } from "@/services/analytics-adapter";
import ButtonWithAnalytics from "@/features/Common/components/ButtonWithAnalytics";
import IconLoader from "@/features/Common/components/IconLoader";
import MessagesIconCTA from "../MessagesIconCTA";
import ProfileIconCTA from "../ProfileIconCTA";
import PayGMedicalAssistanceCTA from "../PayGMedicalAssistanceCTA";
import AdditionalPayGServicesSection from "../AdditionalPayGServicesV2";
import FeaturedServiceOfferingOptions from "../FeaturedServiceOfferingOptions";
import FeaturedServiceOfferingPriceCTA from "../FeaturedServiceOfferingPriceCTA";
import PersonalizedPanel from "@/features/HealthScore/components/PersonalizedPanel/PersonalisedPanel";

interface PayGMemberDashboardProps {
  memberProfile: MemberProfile | null;
  isMemberProfileLoading: boolean;
  memberProfileFetchError: boolean;
}

interface FeaturedPanelProps {
  item: any;
  onPanelClick: (route: string, name: string) => void;
}

export default function PayGMemberDashboard({
  memberProfile,
  isMemberProfileLoading,
  memberProfileFetchError,
}: PayGMemberDashboardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [isIntercareSupported, setIsIntercareSupported] = useState<
    boolean | null
  >(null);
  const [hasActiveSessions, setHasActiveSessions] = useState<boolean>(false);

  useEffect(() => {
    if (memberProfile?.consultation.consultationResponses) {
      setHasActiveSessions(
        memberProfile.consultation.consultationResponses.length > 0
      );
    }
  }, [memberProfile?.consultation.consultationResponses]);

  const isPathologyEnabled = isFeatureEnabled(
    FeatureToggleIdentifier.ENABLE_PATHOLOGY_PANELS
  );

  const isVoucherEnabled = isFeatureEnabled(
    FeatureToggleIdentifier.ENABLE_VOUCHER_REDEMPTION
  );

  const isDocMedBundleEnabled = isFeatureEnabled(
    FeatureToggleIdentifier.ENABLE_DOC_MED_BUNDLES
  );

  const featuredServiceOfferingOptions = useMemo(
    () => FeaturedServiceOfferingOptions(memberProfile, t),
    [memberProfile]
  );

  function onBellIconButtonClick() {
    navigate("/home/messages");
  }

  function onProfileIconCTAClick() {
    trackEvent({
      event: "action.myProfileCTAClicked",
      source: "home screen",
    });

    trackUserInteraction({
      linkText: "Home screen | My profile",
      linkIntent: "navigational",
      linkScope: "button",
    });

    navigate("/home/my-profile");
  }

  function onPanelClick(route: string, name: string) {
    trackUserInteraction({
      linkText: `Home screen | ${name}`,
      linkIntent: "navigational",
      linkScope: "button",
    });

    navigate(route);
  }

  function FeaturedPanel({ item, onPanelClick }: FeaturedPanelProps) {
    return (
      <Stack
        key={item.name}
        sx={{
          ...panelStyle,
          cursor: "pointer",
          backgroundColor: "primary.100",
          border: `2px solid ${theme.palette.primary.main}`,
        }}
        component="button"
        aria-label={item.name}
        spacing={1}
        onClick={() => onPanelClick(item.route, item.name)}
      >
        <Stack
          flexDirection={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          gap={1}
          alignItems="left"
        >
          <Typography variant="h3" fontWeight={600} textAlign="left">
            {item.name}
          </Typography>
          <Stack
            sx={{
              backgroundColor: "accent1.main",
              borderRadius: 2,
              alignSelf: "start",
            }}
            padding={1}
          >
            <Typography
              fontWeight={600}
              variant="body2"
              color="primary.contrastText"
            >
              {t("common.new")}
            </Typography>
          </Stack>
        </Stack>

        <Typography textAlign="left" variant="body1" color="neutral.light">
          <Trans i18nKey={item.description}>
            <Typography
              color="accent1.main"
              fontWeight="bold"
              component="span"
            ></Typography>
          </Trans>
        </Typography>

        <Stack
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
          width="100%"
        >
          {FeaturedServiceOfferingPriceCTA(
            item.type,
            item.amount,
            item.currencyCode,
            item.isDiscountApplied,
            item.originalAmount,
            t
          )}
          <ButtonWithAnalytics
            variant="text"
            size="small"
            endIcon={<IconLoader icon="ChevronNextIcon" />}
          >
            {item.rightButton}
          </ButtonWithAnalytics>
        </Stack>
      </Stack>
    );
  }

  useEffect(() => {
    if (isMemberProfileLoading === false) {
      const membershipConfig = getMembershipConfig(
        memberProfile?.membership?.productCode
      );

      setIsIntercareSupported(
        (isFeatureEnabled(
          FeatureToggleIdentifier.ENABLE_INTERCARE_MEDICAL_ASSISTANCE
        ) &&
          !isFeatureEnabled(FeatureToggleIdentifier.ENABLE_UNIFIED_CARE) &&
          membershipConfig?.isIntercareSupported) ||
          false
      );
    }
  }, [memberProfile, isMemberProfileLoading]);

  return (
    <Stack spacing={2}>
      <Box
        p={2}
        gap={2}
        sx={{
          borderRadius: `0 0 ${convertPxToRem(15)}  ${convertPxToRem(15)}`,
          background: `linear-gradient(to bottom, ${theme.palette.primary.contrastText}, ${theme.palette.accent2.light}, ${theme.palette.accent2.light})`,
          width: "100%",
          boxShadow: shadows.light,
        }}
      >
        <Stack direction="row" width="100%" justifyContent="end" spacing={1}>
          <Stack display="flex" alignItems="end" position="relative">
            <MessagesIconCTA onClick={onBellIconButtonClick} />
          </Stack>
          <Stack display="flex" alignItems="end" position="relative">
            <ProfileIconCTA onClick={onProfileIconCTAClick} />
          </Stack>
        </Stack>
        <Typography
          variant="body1"
          sx={{
            width: "100%",
            fontSize: convertPxToRem(20),
          }}
        >
          {memberProfile && memberProfile?.firstName?.length > 0
            ? t("Home.title.withName", {
                name: memberProfile.firstName,
              })
            : t("Home.title.withoutName")}
        </Typography>
        <Typography
          variant="h1"
          sx={{
            width: "100%",
            fontSize: convertPxToRem(24),
          }}
        >
          {t("Home.helpSubtitle")}
        </Typography>

        {isIntercareSupported && (
          <Stack mt={1} sx={{ zIndex: 2 }}>
            <PayGMedicalAssistanceCTA
              memberProfile={memberProfile}
              isLoading={isMemberProfileLoading}
              onlyDisplayWhenActiveSession={true}
            />
          </Stack>
        )}
      </Box>

      <Stack sx={{ zIndex: 2 }} spacing={2} pl={2} pr={2}>
        <Stack spacing={2}>
          {isFeatureEnabled(FeatureToggleIdentifier.ENABLE_TRENDED_VITALS) && (
            <Stack spacing={2}>
              <PersonalizedPanel />
            </Stack>
          )}

          {isDocMedBundleEnabled &&
            featuredServiceOfferingOptions.length > 0 && (
              <>
                {featuredServiceOfferingOptions.map((item) => (
                  <FeaturedPanel item={item} onPanelClick={onPanelClick} />
                ))}
              </>
            )}

          <AdditionalPayGServicesSection
            memberProfile={memberProfile}
            isMemberProfileLoading={isMemberProfileLoading}
            memberProfileFetchError={memberProfileFetchError}
          />
        </Stack>
      </Stack>
    </Stack>
  );
}
